import { openDialog, replaceDialog } from '@patrianna/dialog-manager'
import { useState, useEffect, useRef } from 'react'

import getSpinWheelOfWinners from 'services/gateway/requests/getSpinWheelOfWinners'
import { getShopOffersSelector } from 'src/store/modules/shop/selectors'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import {
  setWheelFreeSpinsClaimState,
  setWheelNextSpinAt,
  setWheelSpinning,
  decreaseWheelSpins,
} from 'store/modules/bonusesFlow/actions'
import {
  currentWheelCanvasSelector,
  currentWheelSelector,
  isFreeSpinsNotificationReceivedSelector,
} from 'store/modules/bonusesFlow/selectors'
import { getPromoWheelConfig, getWheelCardConfig, getWheelMode } from 'store/modules/brandConfig/selectors'
import { getOffers } from 'store/modules/shop/actions'
import { getUserFreeSpins } from 'store/modules/slotGameFlow/actions'
import { openSnackbar } from 'store/modules/snackbar/actions'

import CelebrationCanvas from './canvas/Celebration'
import WheelCanvas from './canvas/WheelInteractive'
import { isLoyaltyMode } from './config'
import { fallbackWheelCardConfig } from './config/fallbackConfig'
import { snackbarMessage } from './config/rewardSectionConfig'
import { loadFont, getSectionDetails } from './utils'

import type { WHEEL_MODES } from './config'
import type { MutableRefObject } from 'react'
import type { TypedThunk } from 'src/store'
import type {
  SpinWheelOfWinnersResponse,
  WheelSectionDetails,
  SectionDetails,
  SectionType,
} from 'store/modules/bonusesFlow/types'

const useFontsLoader = () => {
  const fontsAsyncRef = useRef<Promise<void> | null>(null)

  if (!fontsAsyncRef.current) {
    fontsAsyncRef.current = Promise.all([
      loadFont('BebasNeue', '/fonts/BebasNeue-Regular.woff2'),
      loadFont('OdudaSoft', '/fonts/Oduda-Bold.woff2'),
    ]).then((): void => undefined)
  }

  return fontsAsyncRef.current
}

export const useWheelApi = () => {
  const dispatch = useAppDispatch()
  const wheel = useAppSelector(currentWheelSelector)
  const wheelCanvasData = useAppSelector(currentWheelCanvasSelector)

  const wheelCode = wheel.code
  const nextSpinDate = wheel.nextSpinAt && new Date(wheel.nextSpinAt)

  const [animated, setAnimated] = useState(false)
  const [error, setError] = useState(false)
  const [sectionDetails, setSectionDetails] = useState<SectionDetails>(null)
  const [messageKey, setMessageKey] = useState<string | null>(null)
  const [winningLabel, setWinningLabel] = useState<string | null>(null)
  const [isTopPrizeWon, setIsTopPrizeWon] = useState(false)

  const setSpinState = (isSpinning: boolean) => {
    dispatch(setWheelSpinning(isSpinning))
  }

  const onSpinEnd = () => {
    setAnimated(true)
    setSpinState(false)
  }

  const spinWheelOfWinnersRequest = (data: typeof wheelCanvasData) => {
    return new Promise<{
      winIndex: number
      nextSpinAt: string
      sectionDetails?: WheelSectionDetails
      sectionType?: SectionType
      messageKey?: string
    }>((res, rej) => {
      const requestCb = (response: Partial<SpinWheelOfWinnersResponse>) => {
        if (response.sectionCode) {
          const winIndex = data.findIndex(({ code }) => code === response.sectionCode)

          res({
            winIndex,
            nextSpinAt: response.nextSpinAt,
            sectionDetails: response.details,
            sectionType: response.sectionType,
            messageKey: response.messageKey,
          })
        } else {
          rej()
        }
      }

      dispatch(getSpinWheelOfWinners(wheelCode, requestCb))
    })
  }

  const getWinnerAsync = async () => {
    dispatch(setWheelFreeSpinsClaimState('claiming'))
    setSpinState(true)

    try {
      const response = await spinWheelOfWinnersRequest(wheelCanvasData)

      if (response.sectionDetails && response.sectionType) {
        setSectionDetails(getSectionDetails(response.sectionType, response.sectionDetails))

        // Find the winning section's label using the winIndex
        const winningSection = wheelCanvasData[response.winIndex]
        setWinningLabel(winningSection?.label || null)
        setIsTopPrizeWon(winningSection?.top)
      }

      setMessageKey(response.messageKey || null)

      dispatch(setWheelNextSpinAt(wheelCode, response.nextSpinAt))

      return response.winIndex
    } catch (err) {
      setError(true)
      setSpinState(false)
      dispatch(setWheelFreeSpinsClaimState('inactive'))

      throw err
    }
  }

  useEffect(() => {
    return () => {
      if (animated) {
        dispatch(decreaseWheelSpins(wheelCode))
      }
    }
  }, [animated, wheelCode])

  useEffect(() => {
    return () => {
      setSpinState(false)
      dispatch(setWheelFreeSpinsClaimState('inactive'))
    }
  }, [])

  return {
    wheelCanvasData,
    nextSpinDate,
    animated,
    error,
    onSpinEnd,
    getWinnerAsync,
    sectionDetails,
    messageKey,
    winningLabel,
    isTopPrizeWon,
  }
}

export const useCanvas = ({
  wheelCanvasData,
  onSpinEnd,
  getWinnerAsync,
  wheelNodeRef,
  celebrationNodeRef,
  wheelLocation,
}: {
  wheelCanvasData: {
    code: string
    label: string
    top: boolean
  }[]
  onSpinEnd: () => void
  getWinnerAsync: () => Promise<number>
  wheelNodeRef: MutableRefObject<HTMLCanvasElement>
  celebrationNodeRef: MutableRefObject<HTMLCanvasElement>
  wheelLocation: 'wheel_of_winners' | 'promo_wheel'
}) => {
  const wheelInstanceRef = useRef(new WheelCanvas())
  const celebrationInstanceRef = useRef(new CelebrationCanvas())
  const config = useAppSelector(getWheelCardConfig) || fallbackWheelCardConfig
  const promoWheelConfig = useAppSelector(getPromoWheelConfig)
  const wheelMode = useAppSelector(getWheelMode)

  const fontLoader = useFontsLoader()

  useEffect(() => {
    if (
      wheelInstanceRef.current &&
      wheelNodeRef.current &&
      celebrationNodeRef.current &&
      celebrationInstanceRef.current &&
      wheelCanvasData
    ) {
      const initCelebration = () => {
        celebrationInstanceRef.current.init(celebrationNodeRef.current, {
          srcSet:
            wheelLocation === 'wheel_of_winners'
              ? config.images.celebration
              : promoWheelConfig.wheelCard.images.celebration,
          params:
            wheelLocation === 'wheel_of_winners'
              ? config.animationConfig.celebration
              : promoWheelConfig.wheelCard.animationConfig.celebration,
        })
      }

      const initWheel = () => {
        wheelInstanceRef.current?.init(wheelNodeRef.current, {
          data: { segments: wheelCanvasData },
          onAnimationEnd: () => {
            onSpinEnd()
            initCelebration()
          },
          /** TODO: makes sense to run this logic at the very beginning of the spinning */
          getWinnerAsync,
          palette:
            wheelLocation === 'wheel_of_winners' ? config?.wheelSkin : promoWheelConfig?.wheelCard?.promoWheelSkin,
          wheelSrc: config?.images?.wheel,
          buttonSrc:
            wheelLocation === 'wheel_of_winners' && isLoyaltyMode(config?.theme)
              ? config?.images?.spinButton
              : promoWheelConfig?.wheelCard?.images?.spinButton,
          mode: (wheelMode as WHEEL_MODES) || null,
        })
      }

      fontLoader.then(() => {
        initWheel()

        // prevent excessive initialization
        wheelInstanceRef.current = null
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wheelCanvasData, config])
}

export const useShowFreeSpinsClaimSnackbar = () => {
  const dispatch = useAppDispatch()
  const freeSpinsNotificationReceived = useAppSelector(isFreeSpinsNotificationReceivedSelector)

  return (messageKey?: string) => {
    dispatch(setWheelFreeSpinsClaimState('inactive'))

    // Check if free spins were not awarded
    if (messageKey && snackbarMessage[messageKey]) {
      dispatch(openSnackbar(snackbarMessage[messageKey]))
    } else if (!freeSpinsNotificationReceived) {
      dispatch(openSnackbar(snackbarMessage['free_spins_not_awarded']))
    }
  }
}

export const useHandleFreeSpinsClaim = () => {
  const dispatch = useAppDispatch()
  const isScEnabled = useAppSelector(sweepstakeEnabledSelector)
  const showFreeSpinsClaimSnackbar = useShowFreeSpinsClaimSnackbar()

  const handleFreeSpinsClaim = (messageKey?: string) => {
    if (!messageKey) {
      dispatch(getUserFreeSpins(isScEnabled))
      openDialog({ dialogName: 'FREE_SPINS_DIALOG' })
    }
    showFreeSpinsClaimSnackbar(messageKey)
  }

  return handleFreeSpinsClaim
}

const getOffersAndShowDialog: TypedThunk = (dispatch, getState) => {
  dispatch(
    getOffers(() => {
      const state = getState()
      const freshOffers = getShopOffersSelector(state)
      const rewardOffer = freshOffers?.find((offer) => offer.offerType === 'reward')

      if (rewardOffer) {
        replaceDialog({ dialogName: 'CUSTOM_OFFER_DIALOG', dialogProps: { openedOffer: rewardOffer } })
      } else {
        replaceDialog({ dialogName: 'EXTRA_GOLD_DIALOG' })
      }
    }),
  )
}

export const useHandlePurchaseOfferClaim = () => {
  const dispatch = useAppDispatch()

  const handlePurchaseOfferClaim = (messageKey?: string) => {
    if (!messageKey) {
      dispatch(getOffersAndShowDialog)
    } else if (messageKey && snackbarMessage[messageKey]) {
      dispatch(openSnackbar(snackbarMessage[messageKey]))
    }
  }

  return handlePurchaseOfferClaim
}
